import { Link } from 'react-router-dom';
import type { OfferOrderState } from '@ecom/contracts';
import { LoadMore, Sheet, Tabs } from '@/components/page';
import { Alert, Pill, Skeleton, type PillTone } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useStatusLabel, useT } from '@/i18n/use-t';
import { count, date, money } from '@/lib/format';
import { useOfferUsageStore } from '@/stores/offer-usage.store';

/**
 * What a coupon or an automatic offer did (ADR-063) — opened from either list.
 *
 * The figures first: how many orders used it and what became of them, how
 * many people, what it took off and what the sold orders came to, and what is
 * left of a coupon's cap. Then the orders themselves, newest first, filtered
 * by what became of them — an abandoned one was placed with it and never paid,
 * or cancelled. Every figure is the server's (ADR-004).
 */

const STATE_TONES: Record<OfferOrderState, PillTone> = { sold: 'success', refunded: 'danger', abandoned: 'warning' };

export function OfferUsageSheet() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const statusLabel = useStatusLabel();
  const { subject, usage, orders, cursor, filter, loading, error, filterBy, loadMore, close } = useOfferUsageStore();
  if (!subject) return null;

  const currency = usage?.currency;
  const fmt = (halalas: number) => money(halalas, locale, currency);

  return (
    <Sheet
      open
      size="lg"
      title={t('usageOf', { name: subject.name })}
      subtitle={subject.kind === 'coupon' ? t('couponUsageBody') : t('offerUsageBody')}
      onClose={close}
    >
      <div className="flex flex-col gap-5">
        {error ? <Alert>{error}</Alert> : null}

        {usage ? (
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Figure
              label={t('usageOrders')}
              value={count(usage.orders, locale)}
              hint={t('usageOrdersSplit', {
                sold: count(usage.sold, locale),
                refunded: count(usage.refunded, locale),
                abandoned: count(usage.abandoned, locale),
              })}
            />
            <Figure
              label={t('usageCustomers')}
              value={count(usage.customers, locale)}
              hint={t('usageGuestOrders', { count: count(usage.guestOrders, locale) })}
            />
            <Figure label={t('usageDiscountGiven')} value={fmt(usage.discountGiven)} hint={t('usageOnSold')} />
            <Figure label={t('revenue')} value={fmt(usage.revenue)} hint={t('usageOnSold')} />
            <Figure label={t('averageOrder')} value={fmt(usage.averageOrder)} />
            {usage.cartsHolding !== null ? (
              // Shoppers who applied it and have not ordered yet (ADR-066).
              <Figure label={t('usageCartsHolding')} value={count(usage.cartsHolding, locale)} hint={t('usageCartsHoldingHint')} />
            ) : null}
            {subject.kind === 'coupon' ? (
              <Figure
                label={t('usageLeft')}
                value={usage.usesLeft === null ? t('usageNoLimit') : count(usage.usesLeft, locale)}
                hint={usage.maxUses === null ? undefined : t('usageOfMax', { max: count(usage.maxUses, locale) })}
              />
            ) : null}
          </dl>
        ) : loading ? (
          <Skeleton className="h-36" />
        ) : null}

        <div>
          <Tabs
            label={t('usageOrders')}
            value={filter || 'all'}
            onChange={(id) => void filterBy(id === 'all' ? '' : id)}
            items={[
              { id: 'all', label: t('allTab'), count: usage?.orders },
              { id: 'sold', label: t('usageSold'), count: usage?.sold },
              { id: 'refunded', label: t('usageRefunded'), count: usage?.refunded },
              { id: 'abandoned', label: t('abandoned'), count: usage?.abandoned },
            ]}
          />
          {loading && orders.length === 0 ? (
            <Skeleton className="mt-3 h-40" />
          ) : orders.length === 0 ? (
            <p className="mt-3 rounded-control bg-surface-sunken px-3 py-2 text-sm text-ink-muted">{t('usageNoOrders')}</p>
          ) : (
            <ul className="mt-3 rounded-control border border-line">
              {orders.map((order) => (
                <li key={order.id} className="flex items-start justify-between gap-3 border-b border-line px-4 py-3 text-sm last:border-0">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link to={`/orders/${order.id}`} dir="ltr" className="font-medium text-brand-700 hover:underline">
                        {order.number}
                      </Link>
                      <Pill tone={STATE_TONES[order.state]}>{statusLabel(order.status)}</Pill>
                    </div>
                    <span className="mt-0.5 block text-ink">
                      {order.customer.id ? (
                        <Link to={`/customers/${order.customer.id}`} className="hover:text-brand-700 hover:underline">
                          {order.customer.name}
                        </Link>
                      ) : (
                        order.customer.name
                      )}
                    </span>
                    <span dir="ltr" className="block truncate text-xs text-ink-muted">
                      {order.customer.email}
                      {order.customer.id ? '' : ` · ${t('guestCustomer')}`}
                    </span>
                  </div>
                  <div className="shrink-0 text-end">
                    <span data-numeric className="block font-medium text-ink">
                      {fmt(order.total)}
                    </span>
                    <span data-numeric className="block text-xs text-success">
                      −{fmt(order.discount)}
                    </span>
                    <span className="block text-xs text-ink-muted">{date(order.placedAt, locale)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <LoadMore visible={Boolean(cursor)} loading={loading} onClick={() => void loadMore()} />
        </div>
      </div>
    </Sheet>
  );
}

function Figure({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-control border border-line p-3">
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd data-numeric className="text-lg font-semibold text-ink">
        {value}
      </dd>
      {hint ? <dd className="text-xs text-ink-muted">{hint}</dd> : null}
    </div>
  );
}
